import type {
  RouteGuidanceOutput,
  RouteInstructionOutput,
  RouteLegOutput,
  RouteOutput,
} from '@azure-rest/maps-route';
import { data } from 'azure-maps-control';
import type GeoJSON from 'geojson';

type Route = RouteOutput;
type RouteLeg = RouteLegOutput;
type RouteGuidance = RouteGuidanceOutput;

interface LatLongPair {
  latitude: number;
  longitude: number;
}

interface IExtendedFeatureCollection extends GeoJSON.FeatureCollection {
  guidance: RouteGuidance;
}

/**
 * Helper function to convert a route response into a GeoJSON FeatureCollection.
 */
const getFeatures = (routes: Route[]): IExtendedFeatureCollection => {
  const bounds: GeoJSON.Position[] = [];
  const features: NonNullable<GeoJSON.Feature>[] = routes.map(
    (route: Route, index: number) => {
      const multiLineCoords = route.legs.map(
        (leg: RouteLeg): GeoJSON.Position[] =>
          leg.points.map((coord: LatLongPair) => {
            const position: GeoJSON.Position = [
              coord.longitude,
              coord.latitude,
            ];
            bounds.push([position[1], position[0]]);
            return position;
          }),
      );

      // Include all properties on the route object except legs.
      // Legs is used to create the MultiLineString, so we only need the summaries.
      // The legSummaries property replaces the legs property with just summary data.
      const props: NonNullable<GeoJSON.GeoJsonProperties> = {
        ...route,
        legSummaries: route.legs.map((leg: RouteLeg) => leg.summary),
        resultIndex: index,
      };
      delete props.legs;

      return {
        type: 'Feature',
        geometry: {
          type: 'MultiLineString',
          coordinates: multiLineCoords,
        },
        properties: props,
      };
    },
  );

  return {
    type: 'FeatureCollection',
    features,
    bbox: data?.BoundingBox.fromLatLngs(bounds) as GeoJSON.BBox,
    guidance: routes[0].guidance || {
      instructions: [] as NonNullable<RouteInstructionOutput>[],
      instructionGroups: [],
    },
  };
};

export default getFeatures;
